import React, { FC, useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import DatePickerDropdown from 'app/components/datepicker-dropdown';
import DatePickerHeaderCustomPeriod from 'app/components/datepicker-dropdown/custom-header/DatePickerHeaderCustomPeriod';
import DatePickerFooter from 'app/components/datepicker-dropdown/DatePickerFooter';
import {
  isDateWithinRange,
  isDateRangeComplete,
} from 'app/components/datepicker-dropdown/utils';
import useSetState from 'utils/hooks/useSetState';
import { getRangePlaceholderLabel } from 'app/components/datepicker-dropdown/utils/placeholder';

type Props = {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  onChange: (
    {
      startDate,
      endDate,
    }: {
      startDate: Dayjs | null;
      endDate: Dayjs | null;
    },
    preset?: string,
  ) => void;
  applyOnChange?: boolean;
};

const RangeDatePicker: FC<Props> = ({
  startDate,
  endDate,
  onChange,
  applyOnChange = false,
}) => {
  const [error, setError] = useSetState<{
    startDate: boolean;
    endDate: boolean;
  }>({
    startDate: true,
    endDate: true,
  });
  const [isOpen, setIsOpen] = useState(false);
  const [startDateTemp, setStartDateTemp] = useState<Dayjs | null>(null);
  const [endDateTemp, setEndDateTemp] = useState<Dayjs | null>(null);

  const onCancelCustomPeriod = () => {
    setStartDateTemp(startDate);
    setEndDateTemp(endDate);
  };

  const onDateRangeSubmit = () => {
    if (isDateRangeComplete(startDateTemp, endDateTemp)) {
      onChange(
        {
          startDate: startDateTemp ?? null,
          endDate: endDateTemp ?? null,
        },
        undefined,
      );
      setIsOpen(false);
    }
  };

  const onRangeSelect = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    const startTemp = start ? dayjs(start) : null;
    const endTemp = end ? dayjs(end) : null;

    setStartDateTemp(startTemp);
    setEndDateTemp(endTemp);

    if (applyOnChange) {
      onChange(
        {
          startDate: startTemp,
          endDate: endTemp,
        },
        undefined,
      );
    }
  };

  const selectedDate = startDate ?? null;
  const isDateComplete = isDateRangeComplete(startDateTemp, endDateTemp);

  useEffect(() => {
    if (startDate && endDate) {
      setError({
        startDate: startDate ? isDateWithinRange(startDate, null, null) : false,
        endDate: endDate ? isDateWithinRange(endDate, null, null) : false,
      });
    }
  }, [startDate, endDate, setError]);

  return (
    <DatePickerDropdown
      contentClassname="tw-min-w-[300px]"
      placeholderClassname="tw-h-[40px]"
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      label={getRangePlaceholderLabel({
        startDateTemp: startDateTemp ?? startDate,
        endDateTemp: endDateTemp ?? endDate,
      })}
      selected={selectedDate?.toDate() ?? null}
      startDate={startDateTemp?.toDate() ?? null}
      endDate={endDateTemp?.toDate() ?? null}
      selectsRange={true}
      onChange={(val) => {
        onRangeSelect(val as [Date | null, Date | null]);
      }}
      renderCustomHeader={(headerProps) => (
        <DatePickerHeaderCustomPeriod
          {...headerProps}
          error={error}
          startDate={startDateTemp ?? startDate}
          endDate={endDateTemp ?? endDate}
          onStartDateChange={(date) => {
            setStartDateTemp(date);
            if (applyOnChange) {
              onChange(
                {
                  startDate: date,
                  endDate: endDateTemp ?? endDate,
                },
                undefined,
              );
            }
          }}
          onEndDateChange={(date) => {
            setEndDateTemp(date);
            if (applyOnChange) {
              onChange(
                {
                  startDate: startDateTemp ?? startDate,
                  endDate: date,
                },
                undefined,
              );
            }
          }}
        />
      )}
    >
      <DatePickerFooter
        isCancelDisabled={
          startDate === startDateTemp && endDate === endDateTemp
        }
        disabled={!isDateComplete}
        onDateRangeSubmit={onDateRangeSubmit}
        onRangeSelectCancel={onCancelCustomPeriod}
        applyOnChange={applyOnChange}
        onDone={() => setIsOpen(false)}
      />
    </DatePickerDropdown>
  );
};

export default RangeDatePicker;
